import moment from "moment";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";

/*
|--------------------------------------------------------------------------
| CEK SHIFT LIBUR
|--------------------------------------------------------------------------
*/
const isDayOffShift = (shift, dayoffFlag = false) => {
  if (!shift && !dayoffFlag) {
    return false;
  }

  const shiftName = String(shift?.name || "")
    .trim()
    .toLowerCase();

  const timeIn = String(shift?.time_in || "").trim();
  const timeOut = String(shift?.time_out || "").trim();

  const isDayoffFlag =
    dayoffFlag === true ||
    dayoffFlag === 1 ||
    dayoffFlag === "1" ||
    String(dayoffFlag).toLowerCase() === "true";

  const isDayOffName =
    shiftName === "dayoff" ||
    shiftName === "day off" ||
    shiftName === "extra off" ||
    shiftName === "extraoff" ||
    shiftName === "off" ||
    shiftName === "libur";

  const isZeroTime =
    (timeIn === "00:00:00" || timeIn === "00:00") &&
    (timeOut === "00:00:00" || timeOut === "00:00");

  return isDayoffFlag || isDayOffName || isZeroTime;
};

/*
|--------------------------------------------------------------------------
| CEK NAMA SCHEDULE LIBUR
|--------------------------------------------------------------------------
*/
const isDayOffSchedule = (schedule) => {
  const scheduleName = String(
    schedule?.schedule_name ||
      schedule?.name ||
      "",
  )
    .trim()
    .toLowerCase();

  return (
    scheduleName === "libur" ||
    scheduleName === "dayoff" ||
    scheduleName === "day off" ||
    scheduleName === "extra off" ||
    scheduleName === "extraoff" ||
    scheduleName === "off"
  );
};

/*
|--------------------------------------------------------------------------
| CARI USER SHIFT BERDASARKAN TANGGAL
|--------------------------------------------------------------------------
*/
const getUserShiftForDate = (item, formattedDate) => {
  return (item.user_shift || []).find(
    (userShift) =>
      moment(userShift.date).format("YYYY-MM-DD") === formattedDate,
  );
};

/*
|--------------------------------------------------------------------------
| CARI SCHEDULE DAY
|--------------------------------------------------------------------------
|
| Jangan pakai format("dddd") karena hasilnya tergantung locale.
| Database menyimpan nama hari bahasa Inggris.
|--------------------------------------------------------------------------
*/
const getScheduleDay = (item, date) => {
  const englishDayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const dayIndex = moment(date).day();
  const dayName = englishDayNames[dayIndex];

  return (item.schedule?.shift_day || []).find(
    (sd) =>
      String(sd?.day_name || "")
        .trim()
        .toLowerCase() ===
      String(dayName || "")
        .trim()
        .toLowerCase(),
  );
};

/*
|--------------------------------------------------------------------------
| CEK LIBUR NASIONAL
|--------------------------------------------------------------------------
*/
const isNationalHoliday = (date, holiday = []) => {
  const formattedDate = moment(date).format("YYYY-MM-DD");

  return (holiday || []).some(
    (holidayItem) =>
      moment(holidayItem.date).format("YYYY-MM-DD") === formattedDate,
  );
};

/*
|--------------------------------------------------------------------------
| DETEKSI STATUS LIBUR
|--------------------------------------------------------------------------
*/
const getHolidayInfo = (item, date, holiday = []) => {
  const formattedDate = moment(date).format("YYYY-MM-DD");

  /*
   * 1. Libur nasional
   */
  if (isNationalHoliday(date, holiday)) {
    return {
      isHoliday: true,
      type: "national_holiday",
      source: "holiday",
      shift: null,
    };
  }

  /*
   * 2. User shift khusus tanggal
   */
  const userShift = getUserShiftForDate(
    item,
    formattedDate,
  );

  if (userShift) {
    if (
      userShift.shift &&
      isDayOffShift(
        userShift.shift,
        userShift.dayoff,
      )
    ) {
      return {
        isHoliday: true,
        type: "day_off",
        source: "user_shift",
        shift: userShift.shift,
      };
    }

    if (userShift.shift) {
      return {
        isHoliday: false,
        type: "working_day",
        source: "user_shift",
        shift: userShift.shift,
      };
    }
  }

  /*
   * 3. Default schedule berdasarkan hari
   */
  const scheduleDay = getScheduleDay(item, date);

  if (scheduleDay) {
    if (
      isDayOffShift(
        scheduleDay.shift,
        scheduleDay.dayoff,
      )
    ) {
      return {
        isHoliday: true,
        type: "day_off",
        source: "schedule_day",
        shift: scheduleDay.shift,
      };
    }

    return {
      isHoliday: false,
      type: "working_day",
      source: "schedule_day",
      shift: scheduleDay.shift,
    };
  }

  /*
   * 4. Data lama: nama schedule langsung Libur / Off
   */
  if (isDayOffSchedule(item.schedule)) {
    return {
      isHoliday: true,
      type: "day_off",
      source: "schedule_name",
      shift: null,
    };
  }

  /*
   * 5. Direct shift
   */
  if (
    item.shift &&
    isDayOffShift(item.shift)
  ) {
    return {
      isHoliday: true,
      type: "day_off",
      source: "direct_shift",
      shift: item.shift,
    };
  }

  if (item.shift) {
    return {
      isHoliday: false,
      type: "working_day",
      source: "direct_shift",
      shift: item.shift,
    };
  }

  return {
    isHoliday: false,
    type: "unknown_working_day",
    source: "fallback",
    shift: null,
  };
};

/*
|--------------------------------------------------------------------------
| CEK APAKAH HARI KERJA
|--------------------------------------------------------------------------
*/
const isWorkingDay = (
  item,
  date,
  holiday = [],
) => {
  const info = getHolidayInfo(
    item,
    date,
    holiday,
  );

  return !info.isHoliday;
};

/*
|--------------------------------------------------------------------------
| COLUMN WIDTH
|--------------------------------------------------------------------------
*/
const getColumnWidths = (rows) => {
  const colWidths = [];

  rows.forEach((row) => {
    row.forEach((cell, colIndex) => {
      const cellValue =
        cell != null
          ? cell.toString()
          : "";

      const cellLength =
        cellValue.length;

      if (!colWidths[colIndex]) {
        colWidths[colIndex] = {
          wch: cellLength,
        };
      } else {
        colWidths[colIndex].wch =
          Math.max(
            colWidths[colIndex].wch,
            cellLength,
          );
      }
    });
  });

  return colWidths;
};

/*
|--------------------------------------------------------------------------
| NORMALIZE LEAVE NAME
|--------------------------------------------------------------------------
*/
const normalizeLeaveTypeName = (leave) =>
  (leave?.leave_type?.name || "")
    .trim()
    .toLowerCase();

/*
|--------------------------------------------------------------------------
| CEK CUTI SETENGAH HARI
|--------------------------------------------------------------------------
*/
const isHalfDayLeaveRecord = (leave) => {
  if (!leave) {
    return false;
  }

  if (
    leave.is_half_day_leave === true ||
    leave.is_half_day_leave === 1 ||
    leave.is_half_day_leave === "1"
  ) {
    return true;
  }

  const name = normalizeLeaveTypeName(leave);

  return (
    name === "cts" ||
    name === "cuti setengah hari" ||
    name === "half day leave" ||
    name.includes("setengah hari") ||
    (name.includes("cuti") && name.includes("setengah"))
  );
};

/*
|--------------------------------------------------------------------------
| AMBIL LEAVE BERDASARKAN TANGGAL
|--------------------------------------------------------------------------
*/
const getLeaveForDate = (item, formattedDate) =>
  (item.leave_paid || [])
    .filter((leave) => {
      const start = moment(leave.date_start).format("YYYY-MM-DD");

      const end = moment(leave.date_end).format("YYYY-MM-DD");

      return formattedDate >= start && formattedDate <= end;
    })
    .sort((a, b) =>
      moment(a.date_start).diff(moment(b.date_start)),
    )[0];

/*
|--------------------------------------------------------------------------
| GET KODE CUTI
|--------------------------------------------------------------------------
*/
const getLeaveCode = (leave) => {
  const rawName = (leave?.leave_type?.name || "").trim();

  const normalized = rawName.toLowerCase();

  if (isHalfDayLeaveRecord(leave)) {
    return "CTS";
  }

  const codeMap = {
    "potong cuti": "CT",
    "cuti tahunan": "CT",
    "cuti menikah": "CM",
    "cuti menikahkan anak": "CMA",
    "cuti khitanan anak": "CKA",
    "cuti baptis anak": "CBA",
    "cuti keluarga meninggal": "CKM",
    "cuti anggota dalam satu rumah meninggal": "CADSRM",
    "cuti melahirkan": "CL",
    "cuti istri melahirkan atau keguguran": "CIM",
    "cuti keguguran": "CK",
    "cuti ibadah haji": "CIH",
    "unpaid leave": "UL",
    terlambat: "LI",
    sakit: "S",
    "work from home": "WFH",
  };

  return codeMap[normalized] || rawName;
};

/*
|--------------------------------------------------------------------------
| CEK STATUS KARYAWAN PADA TANGGAL
|--------------------------------------------------------------------------
*/
const getEmployeeDateStatus = (item, date) => {
  const currentDate = moment(date).startOf("day");

  const joinDate = item.join_date
    ? moment(item.join_date).startOf("day")
    : null;

  const resignDate = item.resign_date
    ? moment(item.resign_date).startOf("day")
    : null;

  const endDate = item.end_date
    ? moment(item.end_date).startOf("day")
    : null;

  if (
    joinDate &&
    currentDate.isBefore(joinDate, "day")
  ) {
    return "Belum Join";
  }

  if (
    resignDate &&
    currentDate.isSameOrAfter(resignDate, "day")
  ) {
    return "Nonaktif";
  }

  if (
    endDate &&
    currentDate.isSameOrAfter(endDate, "day")
  ) {
    return "Nonaktif";
  }

  return "Aktif";
};

const isEmployeeActiveOnDate = (item, date) =>
  getEmployeeDateStatus(item, date) === "Aktif";

/*
|--------------------------------------------------------------------------
| GET LAST ATTENDANCE BY STATUS
|--------------------------------------------------------------------------
*/
const getLastByStatus = (attendances, status) => {
  const filtered = attendances.filter(
    (att) => att.status === status,
  );

  if (filtered.length === 0) {
    return null;
  }

  return filtered.reduce(
    (prev, current) =>
      prev.attendance_id > current.attendance_id
        ? prev
        : current,
  );
};

/*
|--------------------------------------------------------------------------
| EXPORT SUMMARY
|--------------------------------------------------------------------------
*/
export const exportSummary = (
  attendanceData,
  exportStartDate,
  exportEndDate,
  selectedStatus,
  holiday = [],
) => {
  const mainHeader = [
    "No",
    "Employee ID",
    "Name",
    "Department",
    "Branch",
    "Job Position",
    "Job Level",
    "Join Date",
    "Resign Date",
    "End Date",
    "Employment Status",
  ];

  const summaryHeader = [
    "Present",
    "Day Off",
    "National Holiday",
    "Absen",
    "Cuti Setengah Hari",
    "Cuti Tahunan",
    "Sakit",
    "Unpaid Leave",
    "Cuti Istri",
    "Cuti Keluarga",
    "Cuti Melahirkan",
    "Cuti Menikah",
    "Telat",
    "Early Out",
    "No Check In",
    "No Check Out",
  ];

  const startDate = moment(exportStartDate);
  const endDate = moment(exportEndDate);

  const dateRange = [];

  for (
    let d = startDate.clone();
    d.isSameOrBefore(endDate);
    d.add(1, "day")
  ) {
    dateRange.push(d.clone());
  }

  const groupedDates = {};

  dateRange.forEach((d) => {
    const date = d.toDate();

    const monthYear =
      date.toLocaleDateString("id-ID", {
        month: "long",
        year: "numeric",
      });

    if (!groupedDates[monthYear]) {
      groupedDates[monthYear] = [];
    }

    groupedDates[monthYear].push(date);
  });

  const totalColumns =
    mainHeader.length +
    dateRange.length +
    summaryHeader.length;

  const headerRow1 =
    new Array(totalColumns).fill("");

  const headerRow2 =
    new Array(totalColumns).fill("");

  mainHeader.forEach((title, idx) => {
    headerRow2[idx] = title;
  });

  const dailyStartCol = mainHeader.length;

  dateRange.forEach((date, idx) => {
    headerRow2[dailyStartCol + idx] =
      String(date.date()).padStart(2, "0");
  });

  summaryHeader.forEach((title, idx) => {
    headerRow2[
      dailyStartCol +
        dateRange.length +
        idx
    ] = title;
  });

  headerRow1[0] = "Data Karyawan";

  const merges = [
    {
      s: {
        r: 1,
        c: 0,
      },
      e: {
        r: 1,
        c: mainHeader.length - 1,
      },
    },
  ];

  let currentCol = mainHeader.length;

  Object.entries(groupedDates).forEach(
    ([monthYear, dates]) => {
      headerRow1[currentCol] = monthYear;

      dates.forEach((date) => {
        headerRow2[currentCol] =
          String(date.getDate()).padStart(2, "0");

        currentCol++;
      });

      merges.push({
        s: {
          r: 1,
          c: currentCol - dates.length,
        },
        e: {
          r: 1,
          c: currentCol - 1,
        },
      });
    },
  );

  headerRow1[currentCol] = "Status Kehadiran";

  merges.push({
    s: {
      r: 1,
      c: currentCol,
    },
    e: {
      r: 1,
      c:
        currentCol +
        summaryHeader.length -
        1,
    },
  });

  const rows = attendanceData
    .map((item, index) => {
      let countPresent = 0;
      let countNoCheckOut = 0;
      let countLate = 0;
      let countAlpha = 0;
      let countEarlyOut = 0;
      let countNoCheckIn = 0;

      const statusCount = {};

      /*
      |--------------------------------------------------------------------------
      | SUMMARY ABSEN
      |--------------------------------------------------------------------------
      */
      for (
        let date = moment(startDate);
        date.isSameOrBefore(endDate);
        date.add(1, "days")
      ) {
        const attendanceDate =
          date.format("YYYY-MM-DD");

        const attendanceForDate =
          (item.attendance || []).filter(
            (att) =>
              moment(att.date).format("YYYY-MM-DD") ===
              attendanceDate,
          );

        // Tanggal di luar masa aktif tetap kosong/tidak dihitung, kecuali
        // memang ada history attendance pada tanggal tersebut. Ini menjaga
        // data absensi yang tercatat setelah resign_date/end_date tetap tampil.
        if (
          !isEmployeeActiveOnDate(
            item,
            date,
          ) &&
          attendanceForDate.length === 0
        ) {
          continue;
        }

        const leaveForDate =
          getLeaveForDate(
            item,
            attendanceDate,
          );

        const isOnLeave =
          !!leaveForDate;

        const holidayInfo =
          getHolidayInfo(
            item,
            date,
            holiday,
          );

        const officialHoliday =
          isNationalHoliday(
            date,
            holiday,
          );

        const hasCheckIn =
          attendanceForDate.some(
            (att) =>
              att.status === "checkin",
          );

        const hasCheckOut =
          attendanceForDate.some(
            (att) =>
              att.status === "checkout",
          );

        const checkins =
          attendanceForDate.filter(
            (att) =>
              att.status === "checkin",
          );

        const isLate =
          checkins.length > 0 &&
          checkins.every(
            (att) => att.is_late,
          );

        if (
          hasCheckIn ||
          hasCheckOut
        ) {
          countPresent += 1;

          if (isLate) {
            countLate += 1;
          }

          if (!isOnLeave) {
            if (
              hasCheckIn &&
              !hasCheckOut
            ) {
              countNoCheckOut += 1;
            }

            if (
              !hasCheckIn &&
              hasCheckOut
            ) {
              countNoCheckIn += 1;
            }
          }
        } else if (
          !holidayInfo.isHoliday &&
          !officialHoliday &&
          !isOnLeave
        ) {
          countAlpha += 1;
        }
      }

      /*
      |--------------------------------------------------------------------------
      | SUMMARY LEAVE
      |--------------------------------------------------------------------------
      */
      (item.leave_paid || []).forEach(
        (leave) => {
          const leaveStart =
            moment(
              leave.date_start,
            ).startOf("day");

          const leaveEnd =
            moment(
              leave.date_end,
            ).startOf("day");

          if (
            leave.leave_type != null
          ) {
            for (
              let date = moment(
                startDate,
              ).startOf("day");
              date.isSameOrBefore(
                moment(
                  endDate,
                ).startOf("day"),
              );
              date.add(1, "days")
            ) {
              if (
                !isEmployeeActiveOnDate(
                  item,
                  date,
                )
              ) {
                continue;
              }

              if (
                !date.isBetween(
                  leaveStart,
                  leaveEnd,
                  null,
                  "[]",
                )
              ) {
                continue;
              }

              const attendanceDate =
                date.format(
                  "YYYY-MM-DD",
                );

              const attendanceForDate =
                (
                  item.attendance || []
                ).filter(
                  (att) =>
                    moment(
                      att.date,
                    ).format(
                      "YYYY-MM-DD",
                    ) ===
                    attendanceDate,
                );

              const hasCheckIn =
                attendanceForDate.some(
                  (att) =>
                    att.status ===
                    "checkin",
                );

              const hasCheckOut =
                attendanceForDate.some(
                  (att) =>
                    att.status ===
                    "checkout",
                );

              const holidayInfo =
                getHolidayInfo(
                  item,
                  date,
                  holiday,
                );

              if (
                holidayInfo.isHoliday
              ) {
                continue;
              }

              const rawLeaveTypeName =
                (
                  leave.leave_type
                    .name || ""
                ).trim();

              const isHalfDayLeave =
                isHalfDayLeaveRecord(
                  leave,
                );

              const leaveTypeName =
                isHalfDayLeave
                  ? "Cuti Setengah Hari"
                  : rawLeaveTypeName;

              if (
                isHalfDayLeave ||
                (!hasCheckIn &&
                  !hasCheckOut)
              ) {
                statusCount[
                  leaveTypeName
                ] =
                  (statusCount[
                    leaveTypeName
                  ] || 0) + 1;
              }
            }
          }
        },
      );

      /*
      |--------------------------------------------------------------------------
      | BASE EMPLOYEE DATA
      |--------------------------------------------------------------------------
      */
      const base = [
        index + 1,
        item.users_code || "",
        item.name || "",
        item.division?.name || "",
        item.area?.name || "",
        item.position?.name || "",
        item.level_detail?.name || "",

        item.join_date
          ? moment(
              item.join_date,
            ).format("YYYY-MM-DD")
          : "",

        item.resign_date
          ? moment(
              item.resign_date,
            ).format("YYYY-MM-DD")
          : "",

        item.end_date
          ? moment(
              item.end_date,
            ).format("YYYY-MM-DD")
          : "",

        item.employee_status,
      ];

      /*
      |--------------------------------------------------------------------------
      | DAILY ATTENDANCE
      |--------------------------------------------------------------------------
      */
      let totalDayoff = 0;
      let totalNationalHoliday = 0;

      const daily =
        dateRange.map((date) => {
          const formattedDate =
            date.format(
              "YYYY-MM-DD",
            );

          const attendanceForDate =
            (
              item.attendance || []
            ).filter(
              (att) =>
                moment(
                  att.date,
                ).format(
                  "YYYY-MM-DD",
                ) ===
                formattedDate,
            );

          // Sebelum join / setelah resign atau end date tetap kosong bila
          // tidak ada data. Bila ada history attendance, prioritaskan data
          // faktual tersebut agar tetap muncul di export.
          if (
            !isEmployeeActiveOnDate(
              item,
              date,
            ) &&
            attendanceForDate.length === 0
          ) {
            return "";
          }

          const lastCheckIn =
            getLastByStatus(
              attendanceForDate,
              "checkin",
            );

          const lastCheckOut =
            getLastByStatus(
              attendanceForDate,
              "checkout",
            );

          const isLate =
            lastCheckIn
              ? lastCheckIn.is_late
              : false;

          const hasCheckIn =
            !!lastCheckIn;

          const hasCheckOut =
            !!lastCheckOut;

          /*
          |--------------------------------------------------------------------------
          | CUTI / CTS
          |--------------------------------------------------------------------------
          */
          const leave =
            getLeaveForDate(
              item,
              formattedDate,
            );

          const isHalfDayLeave =
            isHalfDayLeaveRecord(
              leave,
            );

          if (
            isHalfDayLeave
          ) {
            if (
              !hasCheckIn &&
              hasCheckOut
            ) {
              return "CTS/NCI";
            }

            if (
              hasCheckIn &&
              !hasCheckOut
            ) {
              return "CTS/NCO";
            }

            if (
              !hasCheckIn &&
              !hasCheckOut
            ) {
              return "CTS/NC";
            }

            return "CTS";
          }

          if (
            leave &&
            leave.leave_type != null &&
            !hasCheckIn &&
            !hasCheckOut
          ) {
            const leaveHolidayInfo =
              getHolidayInfo(
                item,
                date,
                holiday,
              );

            if (
              leaveHolidayInfo.isHoliday
            ) {
              if (
                leaveHolidayInfo.type ===
                "national_holiday"
              ) {
                totalNationalHoliday += 1;
              } else {
                totalDayoff += 1;
              }

              return "Libur";
            }

            return getLeaveCode(
              leave,
            );
          }

          /*
          |--------------------------------------------------------------------------
          | ATTENDANCE NORMAL
          |--------------------------------------------------------------------------
          */
          if (
            hasCheckIn ||
            hasCheckOut
          ) {
            if (
              !hasCheckIn &&
              hasCheckOut
            ) {
              return "H/NCI";
            }

            if (
              hasCheckIn &&
              !hasCheckOut
            ) {
              return isLate
                ? "LI/NCO"
                : "H/NCO";
            }

            if (
              hasCheckIn &&
              hasCheckOut
            ) {
              return isLate
                ? "LI"
                : "H";
            }
          }

          /*
          |--------------------------------------------------------------------------
          | CEK HARI LIBUR
          |--------------------------------------------------------------------------
          */
          const holidayInfo =
            getHolidayInfo(
              item,
              date,
              holiday,
            );

          if (
            holidayInfo.isHoliday
          ) {
            if (
              holidayInfo.type ===
              "national_holiday"
            ) {
              totalNationalHoliday += 1;
            } else {
              totalDayoff += 1;
            }

            return "Libur";
          }

          /*
          |--------------------------------------------------------------------------
          | FALLBACK CUTI
          |--------------------------------------------------------------------------
          */
          if (
            leave &&
            leave.leave_type != null
          ) {
            return getLeaveCode(
              leave,
            );
          }

          /*
          |--------------------------------------------------------------------------
          | ALPHA / ABSEN
          |--------------------------------------------------------------------------
          */
          return "A";
        });

      /*
      |--------------------------------------------------------------------------
      | SUMMARY
      |--------------------------------------------------------------------------
      */
      const summary = [
        countPresent,
        totalDayoff,
        totalNationalHoliday,
        countAlpha,

        statusCount[
          "Cuti Setengah Hari"
        ] || 0,

        statusCount[
          "Cuti Tahunan"
        ] || 0,

        statusCount["Sakit"] || 0,

        statusCount[
          "Unpaid Leave"
        ] || 0,

        statusCount[
          "Cuti Istri"
        ] || 0,

        statusCount[
          "Cuti Keluarga"
        ] || 0,

        statusCount[
          "Cuti Melahirkan"
        ] || 0,

        statusCount[
          "Cuti Menikah"
        ] || 0,

        countLate,
        countEarlyOut,
        countNoCheckIn,
        countNoCheckOut,
      ];

      /*
      |--------------------------------------------------------------------------
      | FILTER EMPLOYEE
      |--------------------------------------------------------------------------
      */
      if (
        !selectedStatus.includes(
          "all",
        )
      ) {
        let include = false;

        if (
          selectedStatus.includes(
            "present",
          ) &&
          countPresent > 0
        ) {
          include = true;
        }

        if (
          selectedStatus.includes(
            "no_clock_out",
          ) &&
          countNoCheckOut > 0
        ) {
          include = true;
        }

        if (
          selectedStatus.includes(
            "late",
          ) &&
          countLate > 0
        ) {
          include = true;
        }

        if (
          selectedStatus.includes(
            "alpha",
          ) &&
          countAlpha > 0
        ) {
          include = true;
        }

        if (!include) {
          return null;
        }
      }

      return [
        ...base,
        ...daily,
        ...summary,
      ];
    })
    .filter(
      (row) => row !== null,
    );

  /*
  |--------------------------------------------------------------------------
  | STYLE
  |--------------------------------------------------------------------------
  */
  const defaultCellStyle = {
    border: {
      top: {
        style: "thin",
        color: {
          rgb: "000000",
        },
      },

      bottom: {
        style: "thin",
        color: {
          rgb: "000000",
        },
      },

      left: {
        style: "thin",
        color: {
          rgb: "000000",
        },
      },

      right: {
        style: "thin",
        color: {
          rgb: "000000",
        },
      },
    },

    alignment: {
      horizontal: "center",
      vertical: "center",
      wrapText: true,
    },
  };

  const titleRow =
    new Array(totalColumns).fill("");

  titleRow[0] =
    "LAPORAN ABSENSI";

  merges.unshift({
    s: {
      r: 0,
      c: 0,
    },
    e: {
      r: 0,
      c: totalColumns - 1,
    },
  });

  const allRows = [
    titleRow,
    headerRow1,
    headerRow2,
    ...rows,
  ];

  const ws =
    XLSX.utils.aoa_to_sheet(
      allRows,
    );

  ws["!merges"] = merges;

  ws["!cols"] =
    getColumnWidths(
      allRows,
    );

  const range =
    XLSX.utils.decode_range(
      ws["!ref"],
    );

  for (
    let R = range.s.r;
    R <= range.e.r;
    ++R
  ) {
    for (
      let C = range.s.c;
      C <= range.e.c;
      ++C
    ) {
      const cellAddress = {
        c: C,
        r: R,
      };

      const cellRef =
        XLSX.utils.encode_cell(
          cellAddress,
        );

      if (!ws[cellRef]) {
        continue;
      }

      if (R === 0) {
        ws[cellRef].s = {
          ...defaultCellStyle,

          font: {
            bold: true,
            sz: 20,
          },
        };
      } else if (R <= 2) {
        ws[cellRef].s = {
          ...defaultCellStyle,

          font: {
            bold: true,
          },

          fill: {
            fgColor: {
              rgb: "D9D9D9",
            },
          },
        };
      } else {
        ws[cellRef].s = {
          ...defaultCellStyle,
        };
      }
    }
  }

  if (ws["A1"]) {
    ws["A1"].s = {
      font: {
        bold: true,
        sz: 20,
      },

      alignment: {
        horizontal: "center",
        vertical: "center",
      },
    };
  }

  /*
  |--------------------------------------------------------------------------
  | CREATE WORKBOOK
  |--------------------------------------------------------------------------
  */
  const wb =
    XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    "Ringkasan Absensi",
  );

  const wbout = XLSX.write(
    wb,
    {
      bookType: "xlsx",
      type: "binary",
    },
  );

  const buf =
    new ArrayBuffer(
      wbout.length,
    );

  const view =
    new Uint8Array(buf);

  for (
    let i = 0;
    i < wbout.length;
    i++
  ) {
    view[i] =
      wbout.charCodeAt(i) &
      0xff;
  }

  saveAs(
    new Blob([buf], {
      type: "application/octet-stream",
    }),

    "Ringkasan_Absensi.xlsx",
  );
};